import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GraphStatusService, GraphStatus } from '@/services/graph-status.service';
import { IndexerStatusService, IndexerStatus } from '@/services/indexer-status.service';
import {
  DataService,
  ExternalOrderbookBidPublication,
  StashBidProtectionLogRow,
  StashBidProtectionLogsPage,
} from '@/services/data.service';
import { Web3Service } from '@/services/web3.service';
import { StateService } from '@/services/state.service';
import { LoaderComponent } from '@/components/icons/loader.component';
import { StashBidsTableComponent, BidFilterState } from '@/components/stash-bids-table/stash-bids-table.component';
import { Subscription, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ChangelogFeature {
  text: string;
  onlyDisplayOn?: string[];
}

export interface ChangelogEntry {
  date: string;
  features: (string | ChangelogFeature)[];
}

@Component({
  standalone: true,
  imports: [CommonModule, LoaderComponent, StashBidsTableComponent],
  selector: 'app-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss']
})
export class StatusComponent implements OnInit, OnDestroy {
  statuses: GraphStatus[] = [];
  indexers: IndexerStatus[] = [];
  bids: any[] = [];
  totalBidsCount = 0;
  bidsCurrentPage = 1;
  readonly bidsItemsPerPage = 20;
  loading = true;
  indexersLoading = true;
  bidsLoading = false;
  lastBidFetch: Date | null = null;
  private bidsFilters: { sortBy?: string; sortOrder?: string; filterStatus?: string; filterSource?: string; filterBidder?: string } = {};
  externalBidPublications: ExternalOrderbookBidPublication[] = [];
  externalBidPublicationsTotal = 0;
  externalBidPublicationsCurrentPage = 1;
  readonly externalBidPublicationsItemsPerPage = 20;
  externalBidPublicationsTotalPages = 0;
  externalBidPublicationsLoading = false;
  externalBidPublicationsAdminVisible = false;

  stashBidProtectionLogs: StashBidProtectionLogRow[] = [];
  stashBidProtectionLogsTotal = 0;
  stashBidProtectionLogsCurrentPage = 1;
  readonly stashBidProtectionLogsItemsPerPage = 20;
  stashBidProtectionLogsTotalPages = 0;
  stashBidProtectionLogsLoading = false;
  stashBidProtectionServerEnabled: boolean | null = null;

  /** Row selected for the stash protection audit detail dialog (admin). */
  stashProtectionLogDetailRow: StashBidProtectionLogRow | null = null;

  private subscription = new Subscription();

  private changelog: ChangelogEntry[] = [
    {
      date: '2026-06-05',
      features: [
        {
          text: 'Punk detail and All Punks grid no longer highlight unfunded stash bids as the top offer; unfunded bids remain visible in full bid lists',
          onlyDisplayOn: ['cryptopunks', 'cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-29',
      features: [
        {
          text: 'PunksMarket on-chain bids appear alongside stash bids on punk pages and the bids market tab, with independent feature flags',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
        {
          text: 'Sellers with a directed PunksMarket listing can accept matching on-chain bids in-app when enabled',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-25',
      features: [
        {
          text: 'Punk Tools sidebar and profile page update wrap status immediately after a confirmed wrap or unwrap transaction',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
        {
          text: 'V1 punk transfers use on-chain wrap status so unwrapped punks transfer correctly, even right after unwrap',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
        {
          text: 'Punk detail page native marketplace section title reflects wrap status (wrapped vs unwrapped)',
          onlyDisplayOn: ['cryptopunks', 'cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-24',
      features: [
        {
          text: 'Punk Tools sidebar refresh with batch "Unwrap All" for wrapped V1 punks',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-23',
      features: [
        {
          text: 'Wrap/Unwrap button on punk detail page opens Punk Tools with the punk pre-selected',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
        {
          text: 'Dashboard owned punks show wrap status; bought and sold sections show sale dates',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-22',
      features: [
        {
          text: 'List, buy, and delist unwrapped V1 punks directly on the marketplace via PunksMarket',
          onlyDisplayOn: ['cryptopunks-v1'],
        },
      ],
    },
    {
      date: '2026-05-15',
      features: [
        'Shared links to a stash bid (from the bid details view) now open that bid reliably, including archived bids and bids not on the current page.',
      ]
    },
    {
      date: '2026-05-14',
      features: [
        'Wallet signature status indicator in header',
        'Migrated wallet connection to Reown AppKit',
        {
          text: 'Stash bid legacy protection (feature-flagged; admin audit log on /status)',
          onlyDisplayOn: ['cryptopunks', 'cryptopunks-v1'],
        },
      ]
    },
    {
      date: '2026-04-17',
      features: [
        {
          text: 'PunkV1/V2 cross-marketplace links',
          onlyDisplayOn: ['cryptopunks', 'cryptopunks-v1']
        }
      ]
    },
    {
      date: '2026-04-06',
      features: [
        'Archived bids display with status filter and archive toggle on all bid pages'
      ]
    },
    {
      date: '2026-03-31',
      features: [
        'Unfunded bid display and management'
      ]
    },
    {
      date: '2026-03-21',
      features: [
        {
          text: 'Disguise bid feature (mask targeted punks on trait bids)',
          onlyDisplayOn: ['cryptopunks']
        }
      ]
    },
    {
      date: '2026-03-14',
      features: [
        'Copy bid feature'
      ]
    },
    {
      date: '2026-03-10',
      features: [
        'Notification bots (Telegram, Discord, X) with image cards, dispatch filters, and custom embed layout'
      ]
    },
    {
      date: '2026-01-27',
      features: [
        '"You\'re not accepting the highest bid" alert on bid acceptance'
      ]
    },
    {
      date: '2026-01-23',
      features: [
        'Bid table redesign, legacy bid merge in sidebar'
      ]
    },
    {
      date: '2026-01-22',
      features: [
        {
          text: 'CryptoPunks V1 release',
          onlyDisplayOn: ['cryptopunks-v1']
        }
      ]
    },
    {
      date: '2026-01-16',
      features: [
        'Outgoing/incoming bid tabs'
      ]
    },
    {
      date: '2026-01-09',
      features: [
        'Delegated wallet bid management, stash + wallet balance on dashboard'
      ]
    },
    {
      date: '2026-01-02',
      features: [
        {
          text: 'ERC721 stash support (V1 unwrapped punks, mainnet deployment)',
          onlyDisplayOn: ['cryptopunks-v1', 'cryptophunks']
        }
      ]
    },
    {
      date: '2025-12-25',
      features: [
        'Bid page with filtering and sorting'
      ]
    },
    {
      date: '2025-12-18',
      features: [
        'Individual bid creation with punk selection ("cart" mode)'
      ]
    },
    {
      date: '2025-12-17',
      features: [
        'Delegate.xyz support, wrap/unwrap button on profile page'
      ]
    },
    {
      date: '2025-12-15',
      features: [
        'Confidential bids (creation, display, acceptance)',
        {
          text: 'CryptoPunks V1 stash bids',
          onlyDisplayOn: ['cryptopunks-v1']
        }
      ]
    },
    {
      date: '2025-12-14',
      features: [
        'Extended stash bids support'
      ]
    },
    {
      date: '2025-11-27',
      features: [
        {
          text: 'Now indexing Gondi loans data',
          onlyDisplayOn: ['cryptopunks']
        }
      ]
    },
    {
      date: '2025-11-14',
      features: [
        'Migrated dashboard sales data (bought/sold events) to use alternative sales indexer service'
      ]
    }, {
      date: '2025-11-11',
      features: [
        'Added a "Display only sales" toggle to the transaction history page',
        'Added a changelog section to track feature additions',
        'Fixed a regression in the item detail page preventing the link to external marketplaces to be properly displayed',
        {
          text: 'Added indexers status section to monitor sales indexer synchronization',
          onlyDisplayOn: ['cryptopunks', 'cryptophunks', 'cryptopunks-v1']
        }
      ]
    }, {
      date: '2025-11-10',
      features: [
        {
          text: 'Migrated the sale history and transaction history to our new indexing services',
          onlyDisplayOn: ['cryptophunks']
        }
      ]
    }, {
      date: '2025-11-10',
      features: [
        {
          text: 'Added NFTfi loan display support (tx history only)',
          onlyDisplayOn: ['cryptopunks']
        }
      ]
    }
  ];

  filteredChangelog: Array<{ date: string; features: string[] }> = [];

  private filterChangelog(): void {
    this.filteredChangelog = this.changelog
      .map(entry => ({
        date: entry.date,
        features: entry.features
          .filter(feature => {
            // If feature is a string, always display it
            if (typeof feature === 'string') {
              return true;
            }
            
            // If feature is an object with onlyDisplayOn
            const featureObj = feature as ChangelogFeature;
            if (!featureObj.onlyDisplayOn || featureObj.onlyDisplayOn.length === 0) {
              // No restriction, display everywhere
              return true;
            }
            
            // Check if current collection is in the onlyDisplayOn array
            return featureObj.onlyDisplayOn.includes(environment.collection);
          })
          .map(feature => typeof feature === 'string' ? feature : feature.text)
      }))
      .filter(entry => entry.features.length > 0); // Remove entries with no features after filtering
  }

  constructor(
    private graphStatusService: GraphStatusService,
    private indexerStatusService: IndexerStatusService,
    private dataService: DataService,
    private stateService: StateService,
    public web3Service: Web3Service
  ) {}

  ngOnInit() {
    // Filter changelog once on initialization
    this.filterChangelog();
    
    // Trigger Web3Service updates when component initializes
    this.triggerWeb3Updates();
    
    // Load graph statuses
    this.loadStatuses();
    
    // Load indexer statuses
    this.loadIndexerStatuses();
    
    // Load all bids (any status) for status dashboard
    this.loadBids();

    // Admin-only section visibility and data loading
    this.watchAdminVisibility();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private triggerWeb3Updates(): void {
    // Force update of current block and last indexed block
    // This will trigger the Web3Service to fetch fresh data
    if (this.web3Service['updateCurrentBlock']) {
      this.web3Service['updateCurrentBlock']();
    }
    if (this.web3Service['updateLastIndexedBlock']) {
      this.web3Service['updateLastIndexedBlock']();
    }
  }

  loadStatuses() {
    this.loading = true;
    this.subscription.add(
      this.graphStatusService.getAllGraphStatuses().subscribe(allStatuses => {
        this.statuses = allStatuses;
        this.loading = false;
      })
    );
  }

  loadIndexerStatuses() {
    this.indexersLoading = true;
    this.subscription.add(
      this.indexerStatusService.getIndexerStatuses().subscribe(indexers => {
        this.indexers = indexers;
        this.indexersLoading = false;
      })
    );
  }

  loadBids() {
    this.bidsLoading = true;
    this.subscription.add(
      this.dataService.fetchAllBidsNoStatusFilter(this.bidsCurrentPage, this.bidsItemsPerPage, this.bidsFilters).subscribe({
        next: ({ bids, total, lastBidFetch }) => {
          this.bids = bids;
          this.totalBidsCount = total;
          this.lastBidFetch = lastBidFetch;
          this.bidsLoading = false;
        },
        error: () => {
          this.bidsLoading = false;
        }
      })
    );
  }

  onBidsPageChange(page: number): void {
    this.bidsCurrentPage = page;
    this.loadBids();
  }

  onBidsFiltersChange(filters: BidFilterState): void {
    this.bidsFilters = {
      sortBy: filters.sortBy || undefined,
      sortOrder: filters.sortOrder || undefined,
      filterStatus: filters.filterStatus || undefined,
      filterSource: filters.filterSource || undefined,
      filterBidder: filters.filterBidder || undefined,
    };
    this.bidsCurrentPage = 1;
    this.loadBids();
  }

  /** True when server-side bids query uses non-default filters/sort (table is hidden if count is 0). */
  get bidsHasActiveFilters(): boolean {
    const f = this.bidsFilters;
    return !!(
      f.sortBy ||
      f.sortOrder ||
      f.filterStatus ||
      f.filterSource ||
      f.filterBidder
    );
  }

  /** Admin + frontend feature flag: show stash bid protection audit subsection. */
  get stashBidProtectionStatusSectionVisible(): boolean {
    return (
      this.externalBidPublicationsAdminVisible &&
      environment.featureFlags?.STASH_BID_PROTECTION === true
    );
  }

  resetBidsFilters(): void {
    this.bidsFilters = {};
    this.bidsCurrentPage = 1;
    this.loadBids();
  }

  private watchAdminVisibility(): void {
    this.subscription.add(
      this.stateService.realWalletAddress$.subscribe((realWalletAddress) => {
        const canSeeAdminSection = this.web3Service.isAdminWallet(realWalletAddress);
        const becameVisible = canSeeAdminSection && !this.externalBidPublicationsAdminVisible;
        this.externalBidPublicationsAdminVisible = canSeeAdminSection;

        if (!canSeeAdminSection) {
          this.closeStashProtectionLogDetailDialog();
          this.externalBidPublications = [];
          this.externalBidPublicationsTotal = 0;
          this.externalBidPublicationsCurrentPage = 1;
          this.externalBidPublicationsTotalPages = 0;
          this.externalBidPublicationsLoading = false;
          this.stashBidProtectionLogs = [];
          this.stashBidProtectionLogsTotal = 0;
          this.stashBidProtectionLogsCurrentPage = 1;
          this.stashBidProtectionLogsTotalPages = 0;
          this.stashBidProtectionLogsLoading = false;
          this.stashBidProtectionServerEnabled = null;
          return;
        }

        if (becameVisible) {
          this.externalBidPublicationsCurrentPage = 1;
          this.loadExternalBidPublications();
          if (environment.featureFlags?.STASH_BID_PROTECTION === true) {
            this.stashBidProtectionLogsCurrentPage = 1;
            this.loadStashBidProtectionLogs();
          }
        }
      })
    );
  }

  loadExternalBidPublications(): void {
    if (!this.externalBidPublicationsAdminVisible) return;

    this.externalBidPublicationsLoading = true;
    this.subscription.add(
      this.dataService
        .fetchExternalOrderbookBidPublications(
          this.externalBidPublicationsCurrentPage,
          this.externalBidPublicationsItemsPerPage,
        )
        .subscribe({
          next: ({ items, total, page, totalPages }) => {
            this.externalBidPublications = items;
            this.externalBidPublicationsTotal = total;
            this.externalBidPublicationsCurrentPage = page;
            this.externalBidPublicationsTotalPages = totalPages;
            this.externalBidPublicationsLoading = false;
          },
          error: () => {
            this.externalBidPublicationsLoading = false;
          },
        }),
    );
  }

  onExternalBidPublicationsPageChange(page: number): void {
    if (!this.externalBidPublicationsAdminVisible) return;
    if (page < 1) return;
    if (this.externalBidPublicationsTotalPages > 0 && page > this.externalBidPublicationsTotalPages) return;
    if (page === this.externalBidPublicationsCurrentPage) return;
    this.externalBidPublicationsCurrentPage = page;
    this.loadExternalBidPublications();
  }

  loadStashBidProtectionLogs(): void {
    if (!this.stashBidProtectionStatusSectionVisible) return;
    this.stashBidProtectionLogsLoading = true;
    this.subscription.add(
      this.dataService
        .fetchStashBidProtectionLogs(
          this.stashBidProtectionLogsCurrentPage,
          this.stashBidProtectionLogsItemsPerPage,
        )
        .subscribe({
          next: (res: StashBidProtectionLogsPage) => {
            this.stashBidProtectionServerEnabled = res.enabled;
            this.stashBidProtectionLogs = res.items;
            this.stashBidProtectionLogsTotal = res.total;
            this.stashBidProtectionLogsCurrentPage = res.page;
            this.stashBidProtectionLogsTotalPages = res.totalPages;
            this.stashBidProtectionLogsLoading = false;
          },
          error: () => {
            this.stashBidProtectionLogsLoading = false;
          },
        }),
    );
  }

  onStashBidProtectionLogsPageChange(page: number): void {
    if (!this.stashBidProtectionStatusSectionVisible) return;
    if (page < 1) return;
    if (this.stashBidProtectionLogsTotalPages > 0 && page > this.stashBidProtectionLogsTotalPages) return;
    if (page === this.stashBidProtectionLogsCurrentPage) return;
    this.closeStashProtectionLogDetailDialog();
    this.stashBidProtectionLogsCurrentPage = page;
    this.loadStashBidProtectionLogs();
  }

  openStashProtectionLogDetailDialog(row: StashBidProtectionLogRow): void {
    this.stashProtectionLogDetailRow = row;
  }

  closeStashProtectionLogDetailDialog(): void {
    this.stashProtectionLogDetailRow = null;
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseStashProtectionDetail(): void {
    if (this.stashProtectionLogDetailRow !== null) {
      this.closeStashProtectionLogDetailDialog();
    }
  }

  getIndexerSyncProgress(indexer: IndexerStatus) {
    return combineLatest([
      this.web3Service.currentBlock$
    ]).pipe(
      map(([latestBlock]) => {
        if (!indexer.isRunning || indexer.blocks.current === 0) {
          return 0;
        }

        if (latestBlock === 0) return 0;
        
        const progress = (indexer.blocks.current / latestBlock) * 100.0;
        return Math.max(0, Math.min(100, progress));
      }),
      startWith(0)
    );
  }

  getIndexerProgress(indexer: IndexerStatus): number {
    return indexer.blocks.progress || 0;
  }

  getSyncProgress(status: GraphStatus) {
    return combineLatest([
      this.web3Service.currentBlock$
    ]).pipe(
      map(([latestBlock]) => {
        if (!status.isHealthy || status.currentBlock === 0) {
          return 0;
        }

        // Calculate progress: how much of the current blockchain we've indexed
        // status.currentBlock = what the graph has indexed
        // latestBlock = current blockchain height
        if (latestBlock === 0) return 0;
        
        // Simple percentage: (indexed blocks / total blocks) * 100
        const progress = (status.currentBlock / latestBlock) * 100.0;
        
        // Ensure progress is between 0 and 100
        return Math.max(0, Math.min(100, progress));
      }),
      startWith(0) // Start with 0% while loading
    );
  }

  getShortUrl(url: string): string {
    if (url.length <= 30) {
      return url;
    }
    return '...' + url.substring(url.length - 30);
  }
}
